// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

// SKU suggestion generation utility for items.
// Generates a suggested SKU from an item name using a deterministic algorithm
// that creates a 4-character prefix from the name and appends a 2-digit serial.

// Stopwords to ignore when tokenizing item names.
const STOPWORDS = new Set([
  'THE',
  'A',
  'AN',
  'OF',
  'AND',
  'FOR',
  'WITH',
  'TO',
  'IN',
  'ON',
  'BY',
]);

// Characters that are not allowed in the SKU prefix.
// I and O are excluded to avoid confusion with 1 and 0.
const DISALLOWED_CHARS = new Set(['I', 'O']);

// Length of the SKU prefix (derived from item name)
const PREFIX_LENGTH = 4;

// Default serial number for frontend suggestions
const DEFAULT_SERIAL = '01';

/**
 * Checks if a character is allowed in the SKU prefix.
 */
function isAllowedChar(char: string): boolean {
  if (DISALLOWED_CHARS.has(char)) {
    return false;
  }
  return /^[A-Z0-9]$/.test(char);
}

/**
 * Tokenizes an item name into an array of uppercase words.
 */
function tokenize(itemName: string): string[] {
  const upperName = itemName.toUpperCase();
  const normalized = upperName.replace(/[^A-Z0-9]/g, ' ');
  const tokens = normalized
    .split(' ')
    .filter((token) => token.length > 0)
    .filter((token) => !STOPWORDS.has(token));
  return tokens;
}

interface SkuSuggestion {
  prefix: string;
  serial: string;
  sku: string;
}

/**
 * Generates a suggested SKU from an item name.
 *
 * The algorithm works in three phases:
 * 1. Extract initials from each word (first allowed character)
 * 2. Fill remaining slots with extra characters from tokens
 * 3. Pad with 'X' if still under 4 characters
 */
export function generateSkuSuggestion(itemName: string): SkuSuggestion {
  const tokens = tokenize(itemName);
  const initialIndices = new Map<number, number>();
  const prefixChars: string[] = [];

  // Phase 1: Extract initials from each word
  for (let tokenIndex = 0; tokenIndex < tokens.length; tokenIndex++) {
    if (prefixChars.length >= PREFIX_LENGTH) {
      break;
    }

    const token = tokens[tokenIndex];
    for (let charIndex = 0; charIndex < token.length; charIndex++) {
      const char = token[charIndex];
      if (isAllowedChar(char)) {
        prefixChars.push(char);
        initialIndices.set(tokenIndex, charIndex);
        break;
      }
    }
  }

  // Phase 2: Fill with extra characters from tokens if needed
  if (prefixChars.length < PREFIX_LENGTH) {
    const candidateList: string[] = [];

    for (let tokenIndex = 1; tokenIndex < tokens.length; tokenIndex++) {
      const token = tokens[tokenIndex];
      const initialIndex = initialIndices.get(tokenIndex) ?? -1;

      for (
        let charIndex = initialIndex + 1;
        charIndex < token.length;
        charIndex++
      ) {
        const char = token[charIndex];
        if (isAllowedChar(char)) {
          candidateList.push(char);
        }
      }
    }

    if (tokens.length > 0) {
      const firstToken = tokens[0];
      const initialIndex = initialIndices.get(0) ?? -1;

      for (
        let charIndex = initialIndex + 1;
        charIndex < firstToken.length;
        charIndex++
      ) {
        const char = firstToken[charIndex];
        if (isAllowedChar(char)) {
          candidateList.push(char);
        }
      }
    }

    for (const char of candidateList) {
      if (prefixChars.length >= PREFIX_LENGTH) {
        break;
      }
      prefixChars.push(char);
    }
  }

  // Phase 3: Pad with 'X' if still under PREFIX_LENGTH
  while (prefixChars.length < PREFIX_LENGTH) {
    prefixChars.push('X');
  }

  const prefix = prefixChars.join('');
  const serial = DEFAULT_SERIAL;
  const sku = prefix + serial;

  return { prefix, serial, sku };
}
